import React, {useEffect, useState} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {Link, useNavigate} from 'react-router-dom';
import {useBaseUrl} from '../utils/utils';

/**
 * Individual/solo practitioner registration (decision E1,
 * dashboard_architecture_plan.md). Per the binding page-design standard
 * (E-bis rule 1): specialization and town are cascading, DB-backed selects
 * fetched from uzima_practitioners' taxonomy/towns endpoints - never a
 * free-text field for an entity that already exists in the database.
 */

type Specialization = { id: number; name: string };
type Subcategory = { id: number; name: string; specializations: Specialization[] };
type Category = { id: number; name: string; subcategories: Subcategory[] };
type Town = { id: number; name: string; county: string };

const ProviderRegister: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();

    const [taxonomy, setTaxonomy] = useState<Category[]>([]);
    const [towns, setTowns] = useState<Town[]>([]);
    const [loadingLookups, setLoadingLookups] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [categoryId, setCategoryId] = useState('');
    const [subcategoryId, setSubcategoryId] = useState('');
    const [form, setForm] = useState({
        first_name: '', last_name: '', email: '', password: '', confirm_password: '',
        phone: '', specialization_id: '', town_id: '', years_experience: '',
        license_number: '', license_expiry: '', bio: '',
    });

    useEffect(() => {
        document.title = 'Register as a Provider - Uzima Healthcare';
        Promise.all([
            axios.get(`${baseUrl}practitioners/specialization-taxonomy/`),
            axios.get(`${baseUrl}practitioners/towns/`),
        ]).then(([taxRes, townRes]) => {
            setTaxonomy(taxRes.data?.data || []);
            setTowns(townRes.data?.data || []);
        }).catch(() => {
            Swal.fire('Error', 'Could not load registration options. Please refresh and try again.', 'error');
        }).finally(() => setLoadingLookups(false));
    }, [baseUrl]);

    const selectedCategory = taxonomy.find((c) => String(c.id) === categoryId);
    const selectedSubcategory = selectedCategory?.subcategories.find((s) => String(s.id) === subcategoryId);

    const handleCategoryChange = (value: string) => {
        setCategoryId(value);
        setSubcategoryId('');
        setForm({...form, specialization_id: ''});
    };

    const handleSubcategoryChange = (value: string) => {
        setSubcategoryId(value);
        setForm({...form, specialization_id: ''});
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (form.password !== form.confirm_password) {
            Swal.fire('Passwords do not match', 'Please re-enter your password.', 'warning');
            return;
        }
        if (!form.specialization_id) {
            Swal.fire('Select a specialization', 'Choose your category, then specialization.', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            const response = await axios.post(`${baseUrl}practitioners/register/`, {
                first_name: form.first_name,
                last_name: form.last_name,
                email: form.email,
                password: form.password,
                phone: form.phone || undefined,
                specialization_id: Number(form.specialization_id),
                town_id: form.town_id ? Number(form.town_id) : undefined,
                years_experience: form.years_experience ? Number(form.years_experience) : 0,
                license_number: form.license_number || undefined,
                license_expiry: form.license_expiry || undefined,
                bio: form.bio || undefined,
            });

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Registration failed');
            }

            await Swal.fire({
                icon: 'success',
                title: 'Registration successful',
                text: 'Your provider account has been created. You can now sign in.',
            });
            navigate('/institution/newlog');
        } catch (error: any) {
            Swal.fire('Registration failed', error?.response?.data?.message || error.message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen d-flex align-items-center justify-content-center py-5" style={{background: 'linear-gradient(135deg, #f8fafc 0%, #ecfdf9 100%)'}}>
            <div className="container" style={{maxWidth: 720}}>
                <div className="card shadow-sm">
                    <div className="card-body p-4 p-md-5">
                        <h2 className="fw-bold mb-1">Register as an Individual Provider</h2>
                        <p className="text-muted mb-4">For solo practitioners - Orthodontists, Cardiologists, Gynaecologists and other independent specialists</p>

                        {loadingLookups ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">First name *</label>
                                        <input className="form-control" required value={form.first_name}
                                               onChange={(e) => setForm({...form, first_name: e.target.value})}/>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Last name *</label>
                                        <input className="form-control" required value={form.last_name}
                                               onChange={(e) => setForm({...form, last_name: e.target.value})}/>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Email *</label>
                                        <input type="email" className="form-control" required value={form.email}
                                               onChange={(e) => setForm({...form, email: e.target.value})}/>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Phone</label>
                                        <input className="form-control" value={form.phone}
                                               onChange={(e) => setForm({...form, phone: e.target.value})}/>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Password *</label>
                                        <input type="password" className="form-control" required value={form.password}
                                               onChange={(e) => setForm({...form, password: e.target.value})}/>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Confirm password *</label>
                                        <input type="password" className="form-control" required value={form.confirm_password}
                                               onChange={(e) => setForm({...form, confirm_password: e.target.value})}/>
                                    </div>
                                </div>

                                <hr className="my-4"/>
                                <h6 className="text-uppercase text-muted small fw-bold mb-3">Specialization</h6>

                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label">Category *</label>
                                        <select className="form-select" required value={categoryId}
                                                onChange={(e) => handleCategoryChange(e.target.value)}>
                                            <option value="">Select...</option>
                                            {taxonomy.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Subcategory *</label>
                                        <select className="form-select" required value={subcategoryId} disabled={!selectedCategory}
                                                onChange={(e) => handleSubcategoryChange(e.target.value)}>
                                            <option value="">{selectedCategory ? 'Select...' : 'Choose a category first'}</option>
                                            {selectedCategory?.subcategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Specialization *</label>
                                        <select className="form-select" required value={form.specialization_id} disabled={!selectedSubcategory}
                                                onChange={(e) => setForm({...form, specialization_id: e.target.value})}>
                                            <option value="">{selectedSubcategory ? 'Select...' : 'Choose a subcategory first'}</option>
                                            {selectedSubcategory?.specializations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Town / Practice location</label>
                                        <select className="form-select" value={form.town_id}
                                                onChange={(e) => setForm({...form, town_id: e.target.value})}>
                                            <option value="">Select...</option>
                                            {towns.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.county})</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Years of experience</label>
                                        <input type="number" min={0} className="form-control" value={form.years_experience}
                                               onChange={(e) => setForm({...form, years_experience: e.target.value})}/>
                                    </div>
                                </div>

                                <hr className="my-4"/>
                                <h6 className="text-uppercase text-muted small fw-bold mb-3">Credentials</h6>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">License number</label>
                                        <input className="form-control" value={form.license_number}
                                               onChange={(e) => setForm({...form, license_number: e.target.value})}/>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">License expiry</label>
                                        <input type="date" className="form-control" value={form.license_expiry}
                                               onChange={(e) => setForm({...form, license_expiry: e.target.value})}/>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="form-label">Short bio</label>
                                    <textarea className="form-control" rows={3} value={form.bio}
                                              onChange={(e) => setForm({...form, bio: e.target.value})}
                                              placeholder="A brief professional summary patients will see"/>
                                </div>

                                <p className="small text-muted mb-3">
                                    Your account will be marked <strong>pending review</strong> until Uzima verifies your
                                    credentials. You'll be notified once verification completes.
                                </p>

                                <button className="btn btn-primary w-100" type="submit" disabled={submitting}>
                                    {submitting ? 'Creating your account…' : 'Register'}
                                </button>

                                <p className="text-center small text-muted mt-3 mb-0">
                                    Already have an account? <Link to="/institution/newlog">Sign in</Link>
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProviderRegister;
